import type { CreatorUserInfo, JournalResponsibleEmployee } from '../types/journal.types'
import {
  formatDateForInput,
  formatTimeForInput,
  parseDateTimeValue,
  parseTimeFromAPI,
} from './dateUtils'
import { formatDateTime as formatDateTimeGlobal } from '@/utils/formatters'

interface PersonWithName {
  last_name: string
  first_name?: string
  middle_name?: string
}

/** ФИО в формате "Фамилия И.О." */
export function formatPersonName(person?: PersonWithName): string {
  if (!person || !person.last_name) {
    return '—'
  }

  const lastName = person.last_name.trim()
  const firstName = person.first_name?.trim() || ''
  const middleName = person.middle_name?.trim() || ''

  const initials: string[] = []

  if (firstName) {
    initials.push(`${firstName[0]}.`)
  }

  if (middleName) {
    initials.push(`${middleName[0]}.`)
  }

  if (initials.length > 0) {
    return `${lastName} ${initials.join('')}`
  }

  return lastName
}

/** ФИО в формате "Фамилия Имя Отчество". */
export function formatFullPersonName(person?: PersonWithName): string {
  if (!person || !person.last_name) {
    return '—'
  }

  const parts = [
    person.last_name,
    person.first_name,
    person.middle_name,
  ].filter(Boolean)

  return parts.length > 0 ? parts.join(' ') : '—'
}

export function formatFullName(user?: CreatorUserInfo): string {
  return formatPersonName(user)
}

/** Дата/время для отображения (дд.мм.гггг, чч:мм). */
export function formatDateTime(dateTimeString: string | null): string {
  if (!dateTimeString || dateTimeString === '—') {
    return '—'
  }

  return formatDateTimeGlobal(dateTimeString) || '—'
}

export function formatDate(dateString: string | null): string {
  if (!dateString) {
    return '—'
  }

  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return '—'
    }

    return date.toLocaleDateString('ru-RU')
  } catch {
    return '—'
  }
}

/** Форматирует значение атрибута для отображения в таблице/форме. */
export function formatAttributeValue(value: unknown, attributeType?: string): string {
  if (value === null || value === undefined) {
    return '—'
  }

  if (attributeType === 'date_time' && (typeof value === 'string' || value instanceof Date)) {
    const parsed = parseDateTimeValue(value)
    if (!parsed) {
      return '—'
    }

    return `${formatDateForInput(parsed)} ${formatTimeForInput(parsed)}`
  }

  if (attributeType === 'time' && typeof value === 'string') {
    try {
      const date = parseTimeFromAPI(value)
      if (date && !Number.isNaN(date.getTime())) {
        return formatTimeForInput(date)
      }

      const timeMatch = value.match(/^(\d{2}):(\d{2})(?::\d{2})?$/)
      if (timeMatch) {
        return `${timeMatch[1]}:${timeMatch[2]}`
      }
    } catch {
    }
  }

  if (typeof value === 'object' && value.id && value.name) {
    const name = String(value.name)
    if (name === 'None' || name === 'null' || name === '') {
      return '—'
    }

    return name
  }

  if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
    const mapped = value.map(item => {
      const itemName = item.name || item
      const stringName = String(itemName)
      if (stringName === 'None' || stringName === 'null' || stringName === '') {
        return '—'
      }

      return stringName
    }).filter(item => item !== '—')

    return mapped.length > 0 ? mapped.join(', ') : '—'
  }

  const stringValue = String(value)
  if (stringValue === 'None' || stringValue === 'null' || stringValue === '') {
    return '—'
  }

  return stringValue
}
